/**
 * 페이지별 SEO 메타데이터 (mimio#59 A안)
 *
 * SPA 라 정적 index.html 한 장이 모든 경로에 나간다. 라우트·데이터에 따라
 * title / description / og / twitter / canonical 을 갱신한다.
 *
 * ⚠️ 이것만으로는 절반이다. 카카오톡·네이버·페이스북 크롤러는 JS 를 실행하지
 * 않으므로 여기서 바꾼 값을 못 본다. 공유 카드까지 고치려면 봇 UA 분기(C안,
 * mimio#59) 가 필요하다.
 *
 * ArtistDetail.vue 에 있던 updateMetaTags() 를 일반화한 것이다. 원본과 달리
 * 라우트를 벗어날 때 기본값으로 되돌린다 — 안 그러면 작가 상세를 보고 나간 뒤
 * 다른 페이지에 작가의 meta 가 그대로 남는다.
 */

const DEFAULT_TITLE = 'mimio | 미묘';
const DEFAULT_DESCRIPTION = '예술과 비즈니스를 연결하는 ART IP PLATFORM';
const DEFAULT_IMAGE =
  'https://article21-images.s3.ap-northeast-2.amazonaws.com/og/og-mimio.png';

/** 목록·기타 페이지의 고정 문구. 라우트 meta.analytics.category 로 고른다. */
const CATEGORY_PRESETS = {
  artist: {
    title: '아티스트 | mimio',
    description: '미묘에서 활동하는 아티스트와 작품을 만나보세요.',
  },
  exhibition: {
    title: '전시 | mimio',
    description: '지금 열리고 있는 전시를 한눈에 확인해보세요.',
  },
  event: {
    title: '이벤트 | mimio',
    description: '미묘가 전하는 이벤트 소식입니다.',
  },
  news: {
    title: '뉴스 | mimio',
    description: '아트 신 소식과 미묘의 뉴스를 전합니다.',
  },
  special: {
    title: '스페셜 | mimio',
    description: '작가와 작품을 깊이 들여다보는 스페셜 콘텐츠입니다.',
  },
};

/** 상세 페이지에서 본문이 비었을 때 쓰는 폴백. */
const DETAIL_FALLBACK_DESCRIPTION = {
  exhibition: '미묘에서 이 전시의 정보를 확인해보세요.',
  event: '미묘에서 이 이벤트의 정보를 확인해보세요.',
  news: '미묘에서 이 소식을 확인해보세요.',
  special: '미묘의 스페셜 콘텐츠를 읽어보세요.',
};

/**
 * HTML 본문에서 description 을 뽑는다.
 * 스페셜은 TipTap 산출물이라 contentHtml 에 태그가 섞여 있다.
 */
export function excerpt(html, maxLength = 160) {
  if (!html) return '';
  const text = String(html)
    .replace(/<[^>]*>/g, ' ') // 태그 제거
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, ' ') // 연속 공백·개행 정리
    .trim();
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trimEnd() + '…';
}

/** 쿼리스트링·해시를 뺀 정규 URL. canonical 과 og:url 에 쓴다. */
function canonicalUrl() {
  if (typeof window === 'undefined') return '';
  return window.location.origin + window.location.pathname;
}

/**
 * URL 에서 그대로 쓸 수 없는 문자를 퍼센트 인코딩한다.
 *
 * 업로드 파일명이 DB 에 원본 그대로 들어가서 공백·한글이 섞인 imageUrl 이 많다(운영 작가 절반).
 * 그대로 og:image 로 내보내면 URL 자체가 유효하지 않아 크롤러가 이미지를 아예 못 받는다 —
 * 공유 카드 이미지가 빈다.
 *
 * encodeURI 는 이미 인코딩된 %XX 를 건드리지 않아 두 번 걸어도 안전하다.
 * 백엔드 SeoRenderService.encodeUrl() 과 같은 규칙이어야 한다 (봇 == 사람).
 */
function encodeUrlSafely(url) {
  if (!url) return url;
  try {
    return encodeURI(url);
  } catch {
    return url; // 손상된 문자열이면 원본 유지 — meta 하나 때문에 페이지가 죽으면 안 된다
  }
}

function setMetaTag(attr, name, content) {
  if (typeof document === 'undefined') return;
  let tag = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function setCanonical(url) {
  if (typeof document === 'undefined') return;
  let tag = document.head.querySelector('link[rel="canonical"]');
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', 'canonical');
    document.head.appendChild(tag);
  }
  tag.setAttribute('href', url);
}

/**
 * 메타데이터를 한 번에 적용한다.
 *
 * @param {object}  meta
 * @param {string} [meta.title]
 * @param {string} [meta.description]
 * @param {string} [meta.image]        절대 URL 이어야 한다
 * @param {string} [meta.type]         og:type — profile / article / website
 * @param {string} [meta.url]          생략하면 현재 경로를 정규화해 쓴다
 */
export function setSeoMeta(meta = {}) {
  const title = meta.title || DEFAULT_TITLE;
  const description = meta.description || DEFAULT_DESCRIPTION;
  const image = encodeUrlSafely(meta.image) || DEFAULT_IMAGE;
  const type = meta.type || 'website';
  const url = meta.url || canonicalUrl();

  if (typeof document !== 'undefined') document.title = title;

  setMetaTag('name', 'description', description);

  setMetaTag('property', 'og:title', title);
  setMetaTag('property', 'og:description', description);
  setMetaTag('property', 'og:image', image);
  setMetaTag('property', 'og:url', url);
  setMetaTag('property', 'og:type', type);

  setMetaTag('name', 'twitter:card', 'summary_large_image');
  setMetaTag('name', 'twitter:title', title);
  setMetaTag('name', 'twitter:description', description);
  setMetaTag('name', 'twitter:image', image);

  setCanonical(url);
}

/**
 * 라우트 이동 직후 호출한다. 이전 페이지의 meta 를 지우고,
 * 목록·고정 페이지면 카테고리 문구까지 여기서 채운다.
 *
 * 상세 페이지는 데이터가 도착한 뒤 setSeoMeta 로 덮어쓴다.
 */
export function resetSeoMeta(route) {
  const category = route?.meta?.analytics?.category;
  const type = route?.meta?.analytics?.type;

  // 상세는 데이터를 기다려야 하므로 기본값만 깔아둔다.
  if (type === 'detail') {
    setSeoMeta({});
    return;
  }

  const preset = CATEGORY_PRESETS[category];
  setSeoMeta(preset ? { title: preset.title, description: preset.description } : {});
}

export function useSeoMeta() {
  return { setSeoMeta, resetSeoMeta, excerpt, DETAIL_FALLBACK_DESCRIPTION };
}

export { DEFAULT_IMAGE, DETAIL_FALLBACK_DESCRIPTION };
